import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { MapPin, Plane, Bus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback caso o pacote não tenha pontos cadastrados
const DEFAULT_BOARDING_POINTS = [
  'Rodoviária Novo Rio',
  'Casa do Alemão na W Luiz',
];

const CheckoutStep3 = () => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState(null);
  const [boardingPoint, setBoardingPoint] = useState('');
  const [cityDeparture, setCityDeparture] = useState('');
  const [packagePoints, setPackagePoints] = useState(null); // pontos ATUAIS do cadastro do pacote

  useEffect(() => {
    const data = sessionStorage.getItem('bookingData');
    if (!data) { navigate('/'); return; }
    const parsed = JSON.parse(data);
    setBookingData(parsed);
    if (parsed.transportMode === 'without_air') {
      const updated = { ...parsed, boardingInfo: { type: 'without_air', boardingPoint: null, cityDeparture: null, note: 'Aéreo não contratado — cliente responsável pelo próprio deslocamento.' } };
      sessionStorage.setItem('bookingData', JSON.stringify(updated));
      navigate('/checkout/contrato', { replace: true });
      return;
    }
    // Busca a configuração ATUAL do pacote (fonte de verdade)
    if (parsed.packageId) {
      axios.get(`${API_URL}/api/packages/${parsed.packageId}`)
        .then(r => setPackagePoints(r.data || null))
        .catch(() => setPackagePoints(null));
    }
  }, [navigate]);

  // Verifica se é rodoviário ou passeio de 1 dia (ambos usam pontos de embarque)
  const isRodoviario = bookingData?.packageCategory === 'rodoviario';
  const isPasseio1Dia = bookingData?.packageCategory === 'passeio-1-dia';
  const usaBoardingPoints = isRodoviario || isPasseio1Dia;

  // Pontos ATUAIS do cadastro do pacote (fonte de verdade)
  const rawPoints = packagePoints?.boarding_points || [];
  const boardingPointsRodoviario = (rawPoints.length
    ? rawPoints.map(p => (typeof p === 'string' ? { name: p, enabled: true } : p)).filter(p => p.enabled !== false).map(p => p.name).filter(Boolean)
    : DEFAULT_BOARDING_POINTS);

  const handleContinue = () => {
    if (usaBoardingPoints && !boardingPoint) {
      alert('Por favor, selecione um ponto de embarque');
      return;
    }
    if (!usaBoardingPoints && !cityDeparture) {
      alert('Por favor, selecione a cidade de embarque');
      return;
    }

    const updatedData = {
      ...bookingData,
      boardingInfo: {
        type: usaBoardingPoints ? 'rodoviario' : 'aereo',
        boardingPoint: usaBoardingPoints ? boardingPoint : null,
        cityDeparture: !usaBoardingPoints ? cityDeparture : null
      }
    };
    
    sessionStorage.setItem('bookingData', JSON.stringify(updatedData));
    navigate('/checkout/contrato');
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 md:py-12">
        {/* Progress */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex items-center justify-center space-x-2 md:space-x-4 min-w-max px-4">
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Seleção</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Dados</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-[#FFD700]"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#FFD700] flex items-center justify-center font-bold text-gray-900 text-xs md:text-sm">3</div>
              <span className="ml-1 md:ml-2 font-medium text-gray-900 text-xs md:text-sm">Embarque</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-300 flex items-center justify-center font-bold text-gray-600 text-xs md:text-sm">4</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Contrato</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-300 flex items-center justify-center font-bold text-gray-600 text-xs md:text-sm">5</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Pagamento</span>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-4 md:p-8">
            <div className="flex items-center space-x-3 mb-6">
              <MapPin className="w-8 h-8 text-[#FFD700]" />
              <h2 className="text-2xl font-bold text-gray-900">Ponto de Embarque</h2>
            </div>

            {/* Info do tipo de viagem */}
            <div className={`mb-6 p-4 rounded-lg ${usaBoardingPoints ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'}`}>
              <div className="flex items-center space-x-3">
                {usaBoardingPoints ? (
                  <Bus className="w-6 h-6 text-orange-600" />
                ) : (
                  <Plane className="w-6 h-6 text-blue-600" />
                )}
                <div>
                  <p className="font-semibold text-gray-900">
                    {isPasseio1Dia ? 'Passeio de 1 Dia' : isRodoviario ? 'Viagem Rodoviária' : 'Viagem Aérea'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {usaBoardingPoints 
                      ? 'Escolha o ponto de embarque mais conveniente para você' 
                      : 'Selecione a cidade de onde você irá embarcar'
                    }
                  </p>
                </div>
              </div>
            </div>

            {usaBoardingPoints ? (
              <div>
                <Label htmlFor="boardingPoint" className="text-base font-medium">
                  Selecione o ponto de embarque *
                </Label>
                <Select value={boardingPoint} onValueChange={setBoardingPoint}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Escolha o ponto de embarque" />
                  </SelectTrigger>
                  <SelectContent>
                    {boardingPointsRodoviario.map((point) => (
                      <SelectItem key={point} value={point}>{point}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-600 mt-3">
                  Horário e detalhes do embarque serão enviados por e-mail e WhatsApp após a confirmação do pagamento.
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="cityDeparture" className="text-base font-medium">
                  Cidade de Embarque *
                </Label>
                <Select value={cityDeparture} onValueChange={setCityDeparture}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Escolha a cidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rio de Janeiro">Rio de Janeiro</SelectItem>
                    <SelectItem value="São Paulo">São Paulo</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-600 mt-3">
                  As passagens aéreas e os detalhes do voo e aeroporto serão enviados por e-mail até 40 dias antes da viagem.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-6 md:pt-8">
              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                className="w-full sm:flex-1 order-2 sm:order-1"
              >
                Voltar
              </Button>
              <Button
                onClick={handleContinue}
                className="w-full sm:flex-1 bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold order-1 sm:order-2"
              >
                <span className="truncate">Continuar para Contrato</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutStep3;
